"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CompositeReportBuilder = void 0;
const MeasureBundleHelpers_1 = require("../helpers/MeasureBundleHelpers");
const Enums_1 = require("../types/Enums");
const uuid_1 = require("uuid");
const AbstractMeasureReportBuilder_1 = require("./AbstractMeasureReportBuilder");
class CompositeReportBuilder extends AbstractMeasureReportBuilder_1.AbstractMeasureReportBuilder {
    constructor(compositeMeasure, options) {
        var _a, _b, _c;
        super(compositeMeasure, options);
        this.compositeScoringType = (_a = (0, MeasureBundleHelpers_1.getCompositeScoringFromMeasure)(compositeMeasure)) !== null && _a !== void 0 ? _a : 'all-or-nothing';
        this.compositeFraction = { numerator: 0, denominator: 0 };
        this.options = options;
        // mapping of component canonical to group(s) and weight(s), defined by CQFM extensions
        this.components = {};
        (_b = compositeMeasure.relatedArtifact) === null || _b === void 0 ? void 0 : _b.forEach(ra => {
            if (ra.resource && ra.type === 'composed-of') {
                // gather group Id and weight extension values
                const groupId = (0, MeasureBundleHelpers_1.getGroupIdForComponent)(ra);
                const weight = (0, MeasureBundleHelpers_1.getWeightForComponent)(ra) || 1;
                if (groupId) {
                    if (!(ra.resource in this.components)) {
                        this.components[ra.resource] = { [groupId]: weight };
                    }
                    else {
                        // cast to Record<string, number> since groupId is defined
                        this.components[ra.resource][groupId] = weight;
                    }
                }
                else {
                    this.components[ra.resource] = weight;
                }
            }
        });
        this.report = {
            resourceType: 'MeasureReport',
            id: (0, uuid_1.v4)(),
            status: 'complete',
            type: 'summary',
            period: {
                start: this.options.measurementPeriodStart,
                end: this.options.measurementPeriodEnd
            },
            measure: (_c = compositeMeasure.url) !== null && _c !== void 0 ? _c : 'UnknownMeasure',
            group: [
                {
                    population: [
                        {
                            count: 0,
                            code: {
                                coding: [
                                    {
                                        system: 'http://terminology.hl7.org/CodeSystem/measure-population',
                                        code: 'denominator'
                                    }
                                ]
                            }
                        },
                        {
                            count: 0,
                            code: {
                                coding: [
                                    {
                                        system: 'http://terminology.hl7.org/CodeSystem/measure-population',
                                        code: 'numerator'
                                    }
                                ]
                            }
                        }
                    ]
                }
            ],
            evaluatedResource: []
        };
    }
    addAllResults(results) {
        // the weighted composite scoring type is component-based, but the others are individual-based
        if (this.compositeScoringType === 'weighted') {
            this.addWeightedResults(results);
        }
        else {
            results.forEach(result => {
                this.addPatientResults(result);
            });
        }
    }
    addWeightedResults(results) {
        // https://build.fhir.org/ig/HL7/cqf-measures/composite-measures.html#weighted-scoring
        // map component canonical to its population results (if only one group exists), or map to a Record that
        // maps each component group to its corresponding weight
        const componentPopulationResults = {};
        results.forEach(result => {
            const componentResults = (0, MeasureBundleHelpers_1.filterComponentResults)(this.components, result.componentResults);
            componentResults.forEach(componentResult => {
                var _a, _b, _c, _d;
                if (componentResult.componentCanonical) {
                    // component info will either consist of mapping of groups to weights, or a single weight
                    const componentInfo = this.components[componentResult.componentCanonical];
                    // if only one weight is specified, then a specific group is not defined
                    const noGroupExt = typeof componentInfo === 'number';
                    if (noGroupExt) {
                        if (!(componentResult.componentCanonical in componentPopulationResults)) {
                            componentPopulationResults[componentResult.componentCanonical] = {
                                numerator: 0,
                                denominator: 0,
                                weight: componentInfo
                            };
                        }
                    }
                    else {
                        // multiple groups are specified for the component
                        Object.keys(this.components[componentResult.componentCanonical]).forEach(groupId => {
                            if (componentResult.componentCanonical) {
                                const weight = this.components[componentResult.componentCanonical][groupId];
                                if (!(componentResult.componentCanonical in componentPopulationResults)) {
                                    componentPopulationResults[componentResult.componentCanonical] = {
                                        [groupId]: {
                                            numerator: 0,
                                            denominator: 0,
                                            weight: weight
                                        }
                                    };
                                }
                                else {
                                    if (!(groupId in componentPopulationResults[componentResult.componentCanonical]))
                                        componentPopulationResults[componentResult.componentCanonical][groupId] = {
                                            numerator: 0,
                                            denominator: 0,
                                            weight: weight
                                        };
                                }
                            }
                        });
                    }
                    if (componentPopulationResults[componentResult.componentCanonical]) {
                        if (((_b = (_a = componentResult.populationResults) === null || _a === void 0 ? void 0 : _a.find(pr => pr.populationType === Enums_1.PopulationType.NUMER)) === null || _b === void 0 ? void 0 : _b.result) === true) {
                            if (noGroupExt) {
                                componentPopulationResults[componentResult.componentCanonical]
                                    .numerator++;
                            }
                            else {
                                componentPopulationResults[componentResult.componentCanonical][componentResult.groupId].numerator++;
                            }
                        }
                        if (((_d = (_c = componentResult.populationResults) === null || _c === void 0 ? void 0 : _c.find(pr => pr.populationType === Enums_1.PopulationType.DENOM)) === null || _d === void 0 ? void 0 : _d.result) === true) {
                            if (noGroupExt) {
                                componentPopulationResults[componentResult.componentCanonical]
                                    .denominator++;
                            }
                            else {
                                componentPopulationResults[componentResult.componentCanonical][componentResult.groupId].denominator++;
                            }
                        }
                    }
                }
            });
            this.addEvaluatedResources(result);
        });
        // Multiply each of the component ratios by the weight associated with the component
        // TODO: this may need to change if an extension is created for non-integer counts on the measure report
        const isComponentResult = (obj) => {
            return 'numerator' in obj;
        };
        Object.values(componentPopulationResults)
            .flatMap(e => (isComponentResult(e) ? e : Object.values(e)))
            .forEach(value => {
            if (value.denominator !== 0) {
                this.compositeFraction.numerator += value.weight * (value.numerator / value.denominator);
            }
            this.compositeFraction.denominator += value.weight;
        });
    }
    addPatientResults(result) {
        var _a;
        // filter component results according to CQFM Group Id extension
        // https://build.fhir.org/ig/HL7/cqf-measures/StructureDefinition-cqfm-groupId.html
        const componentResults = (0, MeasureBundleHelpers_1.filterComponentResults)(this.components, result.componentResults);
        // https://build.fhir.org/ig/HL7/cqf-measures/composite-measures.html#all-or-nothing-scoring
        if (this.compositeScoringType === 'all-or-nothing') {
            let inIpp = false;
            let inDenom = false;
            let inNumer = true;
            componentResults.forEach(componentResult => {
                var _a, _b, _c, _d, _e, _f;
                const ippResult = (_b = (_a = componentResult.populationResults) === null || _a === void 0 ? void 0 : _a.find(pop => pop.populationType === Enums_1.PopulationType.IPP)) === null || _b === void 0 ? void 0 : _b.result;
                if (ippResult === true) {
                    inIpp = true;
                }
                const denomResult = (_d = (_c = componentResult.populationResults) === null || _c === void 0 ? void 0 : _c.find(pop => pop.populationType === Enums_1.PopulationType.DENOM)) === null || _d === void 0 ? void 0 : _d.result;
                if (denomResult === true) {
                    inDenom = true;
                }
                const numerResult = (_f = (_e = componentResult.populationResults) === null || _e === void 0 ? void 0 : _e.find(pop => pop.populationType === Enums_1.PopulationType.NUMER)) === null || _f === void 0 ? void 0 : _f.result;
                if (numerResult === false) {
                    inNumer = false;
                }
            });
            if (inIpp) {
                if (inDenom) {
                    this.compositeFraction.denominator++;
                    if (inNumer) {
                        this.compositeFraction.numerator++;
                    }
                }
            }
        }
        else if (this.compositeScoringType === 'opportunity') {
            // https://build.fhir.org/ig/HL7/cqf-measures/composite-measures.html#opportunity-scoring
            let inIpp = false;
            let inDenom = false;
            componentResults.forEach(componentResult => {
                var _a, _b, _c, _d, _e, _f;
                const ippResult = (_b = (_a = componentResult.populationResults) === null || _a === void 0 ? void 0 : _a.find(pop => pop.populationType === Enums_1.PopulationType.IPP)) === null || _b === void 0 ? void 0 : _b.result;
                if (ippResult === true) {
                    inIpp = true;
                }
                const denomResult = (_d = (_c = componentResult.populationResults) === null || _c === void 0 ? void 0 : _c.find(pop => pop.populationType === Enums_1.PopulationType.DENOM)) === null || _d === void 0 ? void 0 : _d.result;
                if (denomResult === true) {
                    inDenom = true;
                }
                const numerResults = (_f = (_e = componentResult.populationResults) === null || _e === void 0 ? void 0 : _e.filter(pop => pop.populationType === Enums_1.PopulationType.NUMER)) === null || _f === void 0 ? void 0 : _f.map(r => r.result);
                if (inIpp) {
                    if (inDenom) {
                        this.compositeFraction.denominator++;
                    }
                    numerResults === null || numerResults === void 0 ? void 0 : numerResults.forEach(result => {
                        if (result) {
                            this.compositeFraction.numerator++;
                        }
                    });
                }
            });
        }
        else if (this.compositeScoringType === 'linear') {
            // https://build.fhir.org/ig/HL7/cqf-measures/composite-measures.html#patient-level-linear-combination-scoring
            // Always increment the denominator for linear scoring when processing a patient result
            this.compositeFraction.denominator++;
            const [patientDenomCount, patientNumerCount] = (_a = componentResults.reduce((sums, componentResult) => {
                var _a, _b, _c, _d;
                if (((_b = (_a = componentResult.populationResults) === null || _a === void 0 ? void 0 : _a.find(pr => pr.populationType === Enums_1.PopulationType.DENOM)) === null || _b === void 0 ? void 0 : _b.result) === true) {
                    sums[0] += 1;
                }
                if (((_d = (_c = componentResult.populationResults) === null || _c === void 0 ? void 0 : _c.find(pr => pr.populationType === Enums_1.PopulationType.NUMER)) === null || _d === void 0 ? void 0 : _d.result) === true) {
                    sums[1] += 1;
                }
                return sums;
            }, [0, 0])) !== null && _a !== void 0 ? _a : [0, 0];
            if (patientDenomCount !== 0) {
                this.compositeFraction.numerator += (patientNumerCount * 1.0) / patientDenomCount;
            }
        }
        else if (this.compositeScoringType === 'weighted') {
            // https://build.fhir.org/ig/HL7/cqf-measures/composite-measures.html#weighted-scoring
            throw new Error('addPatientResults cannot be used for weighted scoring since it is a component-based composite measure scoring method, addAllResults should be used instead');
        }
        this.addEvaluatedResources(result);
    }
    getReport() {
        // Composite measure population list is a tuple of [denom, numer]
        this.report.group[0].population[0].count = this.compositeFraction.denominator;
        this.report.group[0].population[1].count = this.compositeFraction.numerator;
        this.report.group[0].measureScore = {
            value: this.compositeFraction.denominator > 0
                ? (this.compositeFraction.numerator * 1.0) / this.compositeFraction.denominator
                : 0
        };
        return this.report;
    }
}
exports.CompositeReportBuilder = CompositeReportBuilder;
//# sourceMappingURL=CompositeReportBuilder.js.map